<script setup>
import { router } from '@inertiajs/vue3';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { array, boolean, object, string } from 'yup';

const { tenant, externalService } = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    externalService: {
        required: true,
        type: Object,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const form = reactive({
    active: externalService.active,
    locations: externalService.data.locations,
});

const validationSchema = object({
    active: boolean().required('Ce champ est requis.'),
    locations: array().of(
        object({
            id: string().required('Ce champ est requis.'),
            name: string().required('Ce champ est requis.'),
        }),
    ),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.customer.externalServices.postGoogleMyBusiness',
                {
                    tenant: tenant.id,
                },
            ),
            {
                active: form.active,
                locations: form.locations,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.customer.externalServices.googleMyBusiness',
                    {
                        tenant: tenant.id,
                    },
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.customer.externalServices'),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <SwitchField
                    v-model="form.active"
                    label="Actif"
                    field-name="active"
                    field-id="active"
                />
            </FormContent>

            <FormContent
                v-for="(location, index) in form.locations"
                :key="index"
            >
                <FormGrid>
                    <InputField
                        v-model="form.locations[index].id"
                        label="ID de l'emplacement"
                        :field-name="'locations[' + index + '].id'"
                        :field-id="'locations[' + index + '].id'"
                    />

                    <InputField
                        v-model="location.name"
                        label="Nom de l'emplacement"
                        :field-name="'locations[' + index + '].name'"
                        :field-id="'locations[' + index + '].name'"
                    />
                </FormGrid>

                <ActionButton
                    :callback="
                        () =>
                            form.locations.length > 1
                                ? form.locations.splice(index, 1)
                                : ''
                    "
                    type="secondary"
                >
                    Supprimer
                </ActionButton>
            </FormContent>

            <FormContent>
                <ActionButton
                    :callback="
                        () => form.locations.push({ id: null, name: '' })
                    "
                >
                    Ajouter
                </ActionButton>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors" />
    </FormRoot>
</template>
